import { fetchApi } from '@/services/api-client';
import type { EditorJsContent } from '@/services/editor-js';

export const COMMUNICATIONS_PATH = '/communications';

export type CommunicationAudience = 'all-parents' | 'grades';
export type CommunicationStatus = 'draft' | 'scheduled' | 'sending' | 'sent';
/** Non-English versions only — 'en' is the base row (`title`/`body`), never a translation. */
export type CommunicationTranslationLanguage = 'th' | 'zh';

export interface CommunicationTranslation {
  language: CommunicationTranslationLanguage;
  title: string;
  body: EditorJsContent;
}

export interface CommunicationStats {
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  clickedCount: number;
  readCount: number;
}

/** Matches `CommunicationsService.toSummaryDto` — a hand-mapped allowlist, so a field not
 *  listed there does not exist on the wire even if it looks like it should. */
export interface CommunicationSummary {
  id: string;
  title: string;
  audience: CommunicationAudience;
  gradeLevels: number[] | null;
  includeArrivingFamilies: boolean;
  status: CommunicationStatus;
  recipientCount: number;
  /** Set when a scheduled send was refused and the message reverted to draft. */
  lastError: string | null;
  scheduledFor: string | null;
  sentAt: string | null;
  createdAt: string;
  /** Non-English languages this message has a translation for. Never includes 'en'. */
  translatedLanguages: CommunicationTranslationLanguage[];
}

/** Matches `CommunicationsService.toDetailDto`. */
export interface Communication extends CommunicationSummary {
  body: EditorJsContent;
  stats: CommunicationStats;
  translations: CommunicationTranslation[];
}

/** Matches `CommunicationsService.toRecipientDto` — the recipient row's `parent` relation is
 *  never returned wholesale (it carries a password hash), only these named fields. */
export interface CommunicationRecipient {
  parentId: string;
  parentName: string;
  email: string;
  languageSent: string;
  sentAt: string | null;
  failedAt: string | null;
  failureReason: string | null;
  clickedAt: string | null;
  readAt: string | null;
}

export interface CommunicationInput {
  title: string;
  body: EditorJsContent;
  audience: CommunicationAudience;
  gradeLevels?: number[];
  includeArrivingFamilies?: boolean;
  /**
   * The complete set of non-English versions. A language present is upserted; a language absent
   * is deleted server-side — that is how clearing a tab in the composer takes effect.
   */
  translations?: CommunicationTranslation[];
}

function audienceQuery(
  audience: CommunicationAudience,
  gradeLevels: number[],
  includeArrivingFamilies: boolean
): string {
  const params = new URLSearchParams({ audience });
  if (includeArrivingFamilies) params.set('includeArrivingFamilies', 'true');
  gradeLevels.forEach(grade => params.append('gradeLevels', String(grade)));
  return params.toString();
}

export const communicationsApi = {
  list: () => fetchApi<CommunicationSummary[]>(COMMUNICATIONS_PATH),
  get: (id: string) => fetchApi<Communication>(`${COMMUNICATIONS_PATH}/${id}`),
  recipients: (id: string) => fetchApi<CommunicationRecipient[]>(`${COMMUNICATIONS_PATH}/${id}/recipients`),
  // Declared before `:id` server-side, so `audience/preview` never reaches the detail route.
  previewAudience: (audience: CommunicationAudience, gradeLevels: number[], includeArrivingFamilies: boolean) =>
    fetchApi<{ recipientCount: number }>(
      `${COMMUNICATIONS_PATH}/audience/preview?${audienceQuery(audience, gradeLevels, includeArrivingFamilies)}`
    ),
  create: (input: CommunicationInput) =>
    fetchApi<Communication>(COMMUNICATIONS_PATH, { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<CommunicationInput>) =>
    fetchApi<Communication>(`${COMMUNICATIONS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  remove: (id: string) => fetchApi<void>(`${COMMUNICATIONS_PATH}/${id}`, { method: 'DELETE' }),
  send: (id: string) => fetchApi<{ recipientCount: number }>(`${COMMUNICATIONS_PATH}/${id}/send`, { method: 'POST' }),
  schedule: (id: string, scheduledFor: string) =>
    fetchApi<Communication>(`${COMMUNICATIONS_PATH}/${id}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduledFor }),
    }),
  cancelSchedule: (id: string) =>
    fetchApi<Communication>(`${COMMUNICATIONS_PATH}/${id}/schedule/cancel`, { method: 'POST' }),
};
